import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import * as importApi from '../api/importApi';
import type { ChangeQuery, ConflictQuery } from '../api/importApi';
import type {
  ImportAnalysis,
  ImportChangePage,
  ImportConflictKind,
  ImportConflictPage,
  ImportIntakeResult,
  ImportResolveResult,
  ImportRollbackPreview,
  ImportRollbackResult,
  ImportRunOptions,
  ImportRunSummary,
} from '../model/types';

export const importKeys = {
  all: ['imports'] as const,
  list: () => ['imports', 'list'] as const,
  run: (id: number) => ['imports', 'run', id] as const,
  conflicts: (id: number, query: ConflictQuery) => ['imports', 'conflicts', id, query] as const,
  changes: (id: number, query: ChangeQuery) => ['imports', 'changes', id, query] as const,
  rollback: (id: number) => ['imports', 'rollback', id] as const,
};

/**
 * Unieważnienie po każdej zmianie przebiegu.
 *
 * ⚠ Zasięg jest **szerszy niż import** i to jest celowe: zatwierdzenie i wycofanie ruszają
 * elementy, relacje i właścicieli, więc zmieniają liczby na drzewku, na listach z menu
 * i na pulpicie — w miejscach, których ta mutacja nie dotykała wprost. Zostawienie ich
 * na ekranie znaczyłoby pokazywanie liczb, o których już wiadomo, że są nieprawdziwe.
 */
function invalidateAfterChange(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: importKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['elements'] });
  void queryClient.invalidateQueries({ queryKey: ['commitments'] });
}

export function useImportRuns() {
  return useQuery<ImportRunSummary[]>({
    queryKey: importKeys.list(),
    queryFn: ({ signal }) => importApi.fetchRuns(signal),
  });
}

/**
 * Stan przebiegu.
 *
 * ⭐⭐ **Odpytywanie, a nie powiadomienie** — analiza chodzi w tle (decyzja 107), a mechanizmu
 * powiadamiania w tym produkcie nie ma i nie wolno go założyć. Odpytujemy **tylko wtedy, gdy
 * analiza trwa**: stały interwał na zamkniętym przebiegu byłby ruchem sieciowym bez odbiorcy.
 */
export function useImportRun(runId: number | null) {
  return useQuery<ImportAnalysis>({
    queryKey: importKeys.run(runId ?? 0),
    queryFn: ({ signal }) => importApi.fetchAnalysis(runId as number, signal),
    enabled: runId !== null && Number.isFinite(runId),
    refetchInterval: (query) => (query.state.data?.run.analysisInProgress === true ? 1000 : false),

    // ⚠ ODPYTUJEMY TAKŻE PRZY NIEAKTYWNEJ KARCIE. Domyślnie TanStack wstrzymuje interwał, gdy
    // okno traci ognisko — a przy stu tysiącach elementów przełączenie się na coś innego jest
    // normalnym zachowaniem, nie wyjątkiem. Bez tego admin wraca do ekranu, który wciąż mówi
    // „liczę", choć wynik stoi gotowy od kwadransa, i jedynym wyjściem jest odświeżenie strony.
    refetchIntervalInBackground: true,
  });
}

export function useImportConflicts(runId: number | null, query: ConflictQuery) {
  return useQuery<ImportConflictPage>({
    queryKey: importKeys.conflicts(runId ?? 0, query),
    queryFn: ({ signal }) => importApi.fetchConflicts(runId as number, query, signal),
    enabled: runId !== null && Number.isFinite(runId),
    placeholderData: (previous) => previous,
  });
}

export function useImportChanges(runId: number | null, query: ChangeQuery) {
  return useQuery<ImportChangePage>({
    queryKey: importKeys.changes(runId ?? 0, query),
    queryFn: ({ signal }) => importApi.fetchChanges(runId as number, query, signal),
    enabled: runId !== null && Number.isFinite(runId),
    placeholderData: (previous) => previous,
  });
}

/**
 * Rachunek wycofania.
 *
 * ⚠ `staleTime: 0` — rachunek odpowiada na pytanie „co się stanie, jeśli kliknę **teraz**".
 * Liczba sprzed kwadransa odpowiada na inne pytanie, a to jedyna nieodwracalna operacja
 * w całym imporcie.
 */
export function useRollbackPreview(runId: number | null, enabled: boolean) {
  return useQuery<ImportRollbackPreview>({
    queryKey: importKeys.rollback(runId ?? 0),
    queryFn: ({ signal }) => importApi.fetchRollbackPreview(runId as number, signal),
    enabled: enabled && runId !== null && Number.isFinite(runId),
    staleTime: 0,
  });
}

export function useStartImport() {
  const queryClient = useQueryClient();

  return useMutation<ImportIntakeResult, unknown, File>({
    mutationFn: (file) => importApi.startImport(file),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useReuploadImport(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportIntakeResult, unknown, File>({
    mutationFn: (file) => importApi.reuploadImport(runId, file),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useRequestAnalysis(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportRunSummary, unknown, void>({
    mutationFn: () => importApi.requestAnalysis(runId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

export interface ResolveInput {
  conflictId: number;
  kind: ImportConflictKind;
  choice: string;
}

/**
 * Rozstrzygnięcie jednej pozycji — <b>optimistic</b> (spec 4B).
 *
 * ⭐ Wiersz przestaje czekać <b>od razu</b>, bo admin obrabia setki pozycji i czekanie na
 * odpowiedź przy każdej zamieniłoby ekran w klikanie z przerwami. Migawka → natychmiastowy
 * wynik → <c>onError</c> rollback z komunikatem → <c>onSettled</c> unieważnienie.
 *
 * ⚠ <b>Czego migawka NIE udaje:</b> kaskady na poddrzewo (R2). Liczby pozycji pominiętych
 * przy okazji nie da się zgadnąć po stronie frontu, więc pokazujemy ją dopiero z odpowiedzi
 * serwera — jako toast. Optimistic ma przyspieszać to, co pewne, a nie zmyślać to, czego nie wiemy.
 */
export function useResolveConflict(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportResolveResult, unknown, ResolveInput, { previous: [unknown, unknown][] }>({
    mutationFn: ({ conflictId, kind, choice }) =>
      importApi.resolveConflict(runId, conflictId, kind, choice),

    onMutate: async ({ conflictId, choice }) => {
      await queryClient.cancelQueries({ queryKey: importKeys.all });

      const previous = queryClient.getQueriesData({ queryKey: ['imports', 'conflicts'] });

      queryClient.setQueriesData<ImportConflictPage>({ queryKey: ['imports', 'conflicts'] }, (page) =>
        page === undefined
          ? page
          : {
              ...page,
              items: page.items.map((item) =>
                item.id === conflictId
                  ? { ...item, state: choice === 'skip' ? ('skipped' as const) : ('resolved' as const) }
                  : item,
              ),
            },
      );

      // Licznik „czeka na decyzję" schodzi razem z wierszem — inaczej przycisk zatwierdzenia
      // przez chwilę mówiłby o stanie sprzed kliknięcia.
      queryClient.setQueryData<ImportAnalysis>(importKeys.run(runId), (analysis) =>
        analysis === undefined
          ? analysis
          : {
              ...analysis,
              counts: {
                ...analysis.counts,
                conflictsWaiting: Math.max(0, analysis.counts.conflictsWaiting - 1),
                conflictsResolved:
                  choice === 'skip'
                    ? analysis.counts.conflictsResolved
                    : analysis.counts.conflictsResolved + 1,
                conflictsSkipped:
                  choice === 'skip'
                    ? analysis.counts.conflictsSkipped + 1
                    : analysis.counts.conflictsSkipped,
              },
            },
      );

      return { previous: previous as [unknown, unknown][] };
    },

    onError: (_error, _input, context) => {
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key as readonly unknown[], data);
      });
    },

    onSettled: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

export interface BulkInput {
  kind: ImportConflictKind;
  choice: string;
}

/**
 * Akcja hurtowa na grupie.
 *
 * ⛔ <b>Bez optimistic UI</b>, i to jest wyjątek z powodem: jedno kliknięcie rusza setki pozycji,
 * a przy pominięciu <b>kaskaduje na poddrzewa</b> (R2). Migawka musiałaby zgadnąć zasięg, którego
 * front nie zna — a pokazanie „gotowe" dla liczby, której nikt nie policzył, jest gorsze
 * niż sekunda czekania.
 */
export function useResolveBulk(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportResolveResult, unknown, BulkInput>({
    mutationFn: ({ kind, choice }) => importApi.resolveBulk(runId, kind, choice),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

export interface MapTypeInput {
  fileType: string;
  targetType: string;
}

export function useMapType(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<number, unknown, MapTypeInput>({
    mutationFn: ({ fileType, targetType }) => importApi.mapType(runId, fileType, targetType),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

export interface MapOwnerInput {
  fileOwner: string;
  targetKind: 'user' | 'team' | null;
  targetId: number | null;
}

export function useMapOwner(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<number, unknown, MapOwnerInput>({
    mutationFn: ({ fileOwner, targetKind, targetId }) =>
      importApi.mapOwner(runId, fileOwner, targetKind, targetId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

/**
 * Zmiana opcji przebiegu.
 *
 * ⛔ **Bez optimistic UI**, choć przełącznik wygląda na lokalny: serwer przy tej zmianie
 * PRZELICZA konflikty, więc pokazanie nowego stanu przed odpowiedzią znaczyłoby „ta lista jest
 * już aktualna" w chwili, w której jeszcze nie jest. Migawka i tak byłaby kłamstwem o liczbach,
 * na których stoi decyzja o zatwierdzeniu.
 */
export function useSetImportOptions(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportRunSummary, unknown, ImportRunOptions>({
    mutationFn: (options) => importApi.setImportOptions(runId, options),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: importKeys.all }),
  });
}

/**
 * Zatwierdzenie.
 *
 * ⛔ **Bez optimistic UI** — z tego samego powodu co uruchomienie kampanii: „zapisane"
 * pokazane przed potwierdzeniem serwera znaczy „wsypałem do grafu dziesiątki tysięcy bytów
 * i utworzyłem robotę ludziom, którzy o tym nie wiedzą".
 */
export function useCommitImport(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportRunSummary, unknown, void>({
    mutationFn: () => importApi.commitImport(runId),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useAbandonImport(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportRunSummary, unknown, void>({
    mutationFn: () => importApi.abandonImport(runId),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

export function useRollbackImport(runId: number) {
  const queryClient = useQueryClient();

  return useMutation<ImportRollbackResult, unknown, string[]>({
    mutationFn: (forceReasons) => importApi.rollbackImport(runId, forceReasons),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}
